import React, { useState, useEffect } from 'react';
import { serialApi } from '@/api/serial.api';

import { deriveSerialPrefix } from '@/utils/serialPrefix';

export interface EnteredSerial {
  serialNumber: string;
  vendorSerialNumber?: string;
  imei?: string;
  condition: 'GOOD' | 'DAMAGED_BOX' | 'DEFECTIVE' | 'REFURBISHED';
  warrantyExpiryDate?: string;
  notes?: string;
}

interface SerialEntryModalProps {
  isOpen: boolean;
  onClose: () => void;
  itemName: string;
  itemCode?: string;
  serialPrefix?: string;
  brand?: string;
  requiredQty: number;
  initialSerials?: EnteredSerial[];
  onSave: (serials: EnteredSerial[]) => void;
}

export const SerialEntryModal: React.FC<SerialEntryModalProps> = ({
  isOpen,
  onClose,
  itemName,
  itemCode,
  serialPrefix,
  brand,
  requiredQty,
  initialSerials = [],
  onSave,
}) => {
  const [activeTab, setActiveTab] = useState<'scan' | 'manual' | 'csv'>('scan');
  const [serials, setSerials] = useState<EnteredSerial[]>([]);
  const [scanInput, setScanInput] = useState('');
  const [scanError, setScanError] = useState<string | null>(null);
  const [csvText, setCsvText] = useState('');
  const [isGenerating, setIsGenerating] = useState(false);

  useEffect(() => {
    if (isOpen) {
      if (initialSerials && initialSerials.length > 0) {
        setSerials([...initialSerials]);
      } else {
        setSerials([]);
      }
      setScanInput('');
      setScanError(null);
    }
  }, [isOpen, initialSerials]);

  if (!isOpen) return null;

  const enteredCount = serials.length;
  const isComplete = enteredCount === requiredQty;
  const progressPercent = Math.min(100, Math.round((enteredCount / Math.max(requiredQty, 1)) * 100));

  const handleScanSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    const val = scanInput.trim();
    if (!val) return;

    if (serials.length >= requiredQty) {
      setScanError(`Maximum quantity reached (${requiredQty} units).`);
      return;
    }

    const duplicate = serials.some(
      (s) => s.serialNumber.toLowerCase() === val.toLowerCase() ||
             (s.vendorSerialNumber && s.vendorSerialNumber.toLowerCase() === val.toLowerCase()) ||
             (s.imei && s.imei.toLowerCase() === val.toLowerCase())
    );

    if (duplicate) {
      setScanError(`Duplicate serial scanned: "${val}"`);
      return;
    }

    setScanError(null);
    setSerials([
      ...serials,
      {
        serialNumber: val,
        vendorSerialNumber: val,
        condition: 'GOOD',
      },
    ]);
    setScanInput('');
  };

  const handleRemoveSerial = (index: number) => {
    const next = [...serials];
    next.splice(index, 1);
    setSerials(next);
  };

  const handleUpdateSerial = (index: number, field: keyof EnteredSerial, value: string) => {
    const next = [...serials];
    next[index] = { ...next[index], [field]: value };
    setSerials(next);
  };

  const handleAddManualRow = () => {
    if (serials.length >= requiredQty) return;
    setSerials([
      ...serials,
      {
        serialNumber: '',
        vendorSerialNumber: '',
        condition: 'GOOD',
      },
    ]);
  };

  const handleAutoGenerate = async () => {
    try {
      setIsGenerating(true);
      const remainingNeeded = requiredQty - serials.length;
      if (remainingNeeded <= 0) return;

      const prefixToUse = serialPrefix || deriveSerialPrefix(itemName, itemCode, brand);
      const newSerials: EnteredSerial[] = [];

      // Fetch starting sequence from backend
      const res = await serialApi.generateSerialNumber(undefined, prefixToUse);
      const baseSn = res.serialNumber;
      const match = baseSn.match(/^(SN-.*?-)(\d+)$/);

      if (match) {
        const prefixPart = match[1];
        const startSeq = parseInt(match[2], 10);
        const padLen = match[2].length;

        for (let i = 0; i < remainingNeeded; i++) {
          const serialNo = `${prefixPart}${String(startSeq + i).padStart(padLen, '0')}`;
          newSerials.push({
            serialNumber: serialNo,
            condition: 'GOOD',
          });
        }
      } else {
        for (let i = 0; i < remainingNeeded; i++) {
          newSerials.push({
            serialNumber: `${baseSn}-${i + 1}`,
            condition: 'GOOD',
          });
        }
      }

      setSerials([...serials, ...newSerials]);
    } catch (err: any) {
      alert(`Auto-generation error: ${err.message}`);
    } finally {
      setIsGenerating(false);
    }
  };

  const handleCsvImport = () => {
    if (!csvText.trim()) return;
    const lines = csvText.split(/\r?\n/).map(l => l.trim()).filter(Boolean);
    const imported: EnteredSerial[] = [];

    for (const line of lines) {
      if (serials.length + imported.length >= requiredQty) break;
      const parts = line.split(',').map(p => p.trim());
      const sn = parts[0];
      const vendorSn = parts[1] || '';
      const imei = parts[2] || '';
      const warranty = parts[3] || '';

      if (sn) {
        imported.push({
          serialNumber: sn,
          vendorSerialNumber: vendorSn || undefined,
          imei: imei || undefined,
          condition: 'GOOD',
          warrantyExpiryDate: warranty || undefined,
        });
      }
    }

    setSerials([...serials, ...imported]);
    setCsvText('');
    setActiveTab('manual');
  };

  const handleConfirm = () => {
    if (serials.length !== requiredQty) {
      alert(`Please enter exactly ${requiredQty} serial number(s). Currently entered: ${serials.length}.`);
      return;
    }
    const emptyCheck = serials.some(s => !s.serialNumber.trim());
    if (emptyCheck) {
      alert('All entered rows must have a valid Serial Number.');
      return;
    }
    onSave(serials);
    onClose();
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 backdrop-blur-sm p-4 animate-in fade-in duration-200">
      <div className="bg-white rounded-2xl shadow-2xl border border-gray-100 w-full max-w-4xl max-h-[90vh] flex flex-col overflow-hidden">
        {/* Header */}
        <div className="px-6 py-4 border-b border-gray-100 flex items-center justify-between bg-gradient-to-r from-blue-50/50 to-indigo-50/50">
          <div>
            <div className="flex items-center gap-2">
              <span className="p-1.5 bg-blue-600 text-white rounded-lg text-sm">
                <i className="ri-qr-code-line"></i>
              </span>
              <h2 className="text-lg font-bold text-gray-900">Serial Numbers Entry</h2>
            </div>
            <p className="text-xs text-gray-500 mt-0.5">
              Item: <span className="font-semibold text-gray-700">{itemName}</span> {itemCode ? `(${itemCode})` : ''} • Required: <span className="font-semibold text-blue-600">{requiredQty} units</span>
            </p>
          </div>
          <button
            onClick={onClose}
            className="w-8 h-8 rounded-full flex items-center justify-center text-gray-400 hover:text-gray-600 hover:bg-gray-100 transition-colors"
          >
            <i className="ri-close-line text-lg"></i>
          </button>
        </div>

        {/* Progress bar */}
        <div className="px-6 py-3 bg-gray-50/80 border-b border-gray-100 flex items-center justify-between gap-4">
          <div className="flex-1">
            <div className="flex justify-between text-xs font-semibold mb-1.5">
              <span className={isComplete ? 'text-emerald-700' : 'text-gray-700'}>
                Progress: {enteredCount} / {requiredQty} Serials Entered
              </span>
              <span className={isComplete ? 'text-emerald-700' : 'text-blue-600'}>
                {progressPercent}%
              </span>
            </div>
            <div className="w-full bg-gray-200 rounded-full h-2 overflow-hidden">
              <div
                className={`h-full transition-all duration-300 rounded-full ${
                  isComplete ? 'bg-emerald-500' : 'bg-blue-600'
                }`}
                style={{ width: `${progressPercent}%` }}
              ></div>
            </div>
          </div>

          <div className="flex items-center gap-2">
            <button
              type="button"
              onClick={handleAutoGenerate}
              disabled={isGenerating || isComplete}
              className="px-3 py-1.5 text-xs font-medium bg-amber-500 hover:bg-amber-600 disabled:opacity-50 text-white rounded-lg flex items-center gap-1.5 shadow-sm transition-all"
            >
              <i className="ri-flashlight-line"></i>
              {isGenerating ? 'Generating...' : 'Auto-Fill Next'}
            </button>
          </div>
        </div>

        {/* Navigation Tabs */}
        <div className="flex border-b border-gray-200 px-6 gap-2 pt-2 bg-white">
          <button
            type="button"
            onClick={() => setActiveTab('scan')}
            className={`pb-2.5 px-3 text-xs font-semibold flex items-center gap-1.5 border-b-2 transition-colors ${
              activeTab === 'scan'
                ? 'border-blue-600 text-blue-600'
                : 'border-transparent text-gray-500 hover:text-gray-800'
            }`}
          >
            <i className="ri-barcode-box-line text-sm"></i>
            Barcode Scan Mode
          </button>
          <button
            type="button"
            onClick={() => setActiveTab('manual')}
            className={`pb-2.5 px-3 text-xs font-semibold flex items-center gap-1.5 border-b-2 transition-colors ${
              activeTab === 'manual'
                ? 'border-blue-600 text-blue-600'
                : 'border-transparent text-gray-500 hover:text-gray-800'
            }`}
          >
            <i className="ri-edit-line text-sm"></i>
            Manual Entry ({serials.length})
          </button>
          <button
            type="button"
            onClick={() => setActiveTab('csv')}
            className={`pb-2.5 px-3 text-xs font-semibold flex items-center gap-1.5 border-b-2 transition-colors ${
              activeTab === 'csv'
                ? 'border-blue-600 text-blue-600'
                : 'border-transparent text-gray-500 hover:text-gray-800'
            }`}
          >
            <i className="ri-file-excel-2-line text-sm"></i>
            Bulk CSV / Paste
          </button>
        </div>

        {/* Tab Body */}
        <div className="flex-1 overflow-y-auto p-6">
          {/* TAB 1: SCAN MODE */}
          {activeTab === 'scan' && (
            <div className="space-y-4">
              <form onSubmit={handleScanSubmit} className="flex gap-2">
                <div className="relative flex-1">
                  <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none text-gray-400">
                    <i className="ri-scan-2-line text-lg"></i>
                  </div>
                  <input
                    type="text"
                    autoFocus
                    value={scanInput}
                    onChange={(e) => setScanInput(e.target.value)}
                    disabled={isComplete}
                    placeholder={
                      isComplete
                        ? 'All required serials entered!'
                        : 'Scan barcode or type Vendor Serial / IMEI and press Enter...'
                    }
                    className="w-full pl-10 pr-4 py-2.5 text-sm rounded-xl border border-gray-300 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent disabled:bg-gray-100 disabled:cursor-not-allowed"
                  />
                </div>
                <button
                  type="submit"
                  disabled={!scanInput.trim() || isComplete}
                  className="px-4 py-2.5 bg-blue-600 hover:bg-blue-700 disabled:opacity-50 text-white rounded-xl text-xs font-semibold flex items-center gap-1.5 shadow-sm transition-all"
                >
                  <i className="ri-add-line"></i> Add
                </button>
              </form>

              {scanError && (
                <div className="p-3 bg-rose-50 border border-rose-200 rounded-xl flex items-center gap-2 text-rose-700 text-xs animate-shake">
                  <i className="ri-error-warning-line text-base"></i>
                  <span>{scanError}</span>
                </div>
              )}

              {/* Scanned Serials Quick View */}
              <div className="border border-gray-200 rounded-xl overflow-hidden">
                <table className="w-full text-xs text-left">
                  <thead className="bg-gray-50 text-gray-600 font-semibold border-b border-gray-200">
                    <tr>
                      <th className="py-2.5 px-3 w-12 text-center">#</th>
                      <th className="py-2.5 px-3">Serial / Scanned Code</th>
                      <th className="py-2.5 px-3">Condition</th>
                      <th className="py-2.5 px-3 w-16 text-center">Action</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-100">
                    {serials.length === 0 ? (
                      <tr>
                        <td colSpan={4} className="py-8 text-center text-gray-400">
                          <i className="ri-barcode-line text-3xl block mb-2 opacity-50"></i>
                          No serials scanned yet. Aim your barcode scanner or enter above.
                        </td>
                      </tr>
                    ) : (
                      serials.map((s, idx) => (
                        <tr key={idx} className="hover:bg-blue-50/40">
                          <td className="py-2 px-3 text-center text-gray-400 font-mono">{idx + 1}</td>
                          <td className="py-2 px-3 font-mono font-medium text-gray-900">{s.serialNumber}</td>
                          <td className="py-2 px-3">
                            <span className="px-2 py-0.5 rounded-full text-[10px] font-semibold bg-emerald-50 text-emerald-700 border border-emerald-200">
                              {s.condition}
                            </span>
                          </td>
                          <td className="py-2 px-3 text-center">
                            <button
                              type="button"
                              onClick={() => handleRemoveSerial(idx)}
                              className="text-gray-400 hover:text-rose-600 transition-colors p-1"
                            >
                              <i className="ri-delete-bin-line"></i>
                            </button>
                          </td>
                        </tr>
                      ))
                    )}
                  </tbody>
                </table>
              </div>
            </div>
          )}

          {/* TAB 2: MANUAL MODE */}
          {activeTab === 'manual' && (
            <div className="space-y-4">
              <div className="flex justify-between items-center">
                <p className="text-xs text-gray-500">Edit fields directly in the table below:</p>
                <button
                  type="button"
                  onClick={handleAddManualRow}
                  disabled={isComplete}
                  className="px-3 py-1.5 text-xs font-medium bg-gray-100 hover:bg-gray-200 disabled:opacity-50 text-gray-700 rounded-lg flex items-center gap-1 transition-colors"
                >
                  <i className="ri-add-line"></i> Add Row
                </button>
              </div>

              <div className="border border-gray-200 rounded-xl overflow-x-auto">
                <table className="w-full text-xs text-left min-w-[650px]">
                  <thead className="bg-gray-50 text-gray-600 font-semibold border-b border-gray-200">
                    <tr>
                      <th className="py-2.5 px-3 w-10 text-center">#</th>
                      <th className="py-2.5 px-3">Serial No *</th>
                      <th className="py-2.5 px-3">Vendor SN</th>
                      <th className="py-2.5 px-3">IMEI</th>
                      <th className="py-2.5 px-3 w-28">Condition</th>
                      <th className="py-2.5 px-3 w-32">Warranty Expiry</th>
                      <th className="py-2.5 px-3 w-12 text-center">Del</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-100">
                    {serials.length === 0 ? (
                      <tr>
                        <td colSpan={7} className="py-8 text-center text-gray-400">
                          No serial rows. Click "Add Row" or "Auto-Fill Next".
                        </td>
                      </tr>
                    ) : (
                      serials.map((s, idx) => (
                        <tr key={idx} className="hover:bg-gray-50">
                          <td className="py-2 px-3 text-center text-gray-400 font-mono">{idx + 1}</td>
                          <td className="py-1.5 px-2">
                            <input
                              type="text"
                              value={s.serialNumber}
                              onChange={(e) => handleUpdateSerial(idx, 'serialNumber', e.target.value)}
                              placeholder="e.g. SN-0001"
                              className="w-full px-2 py-1 border border-gray-200 rounded font-mono text-xs focus:ring-1 focus:ring-blue-500 focus:border-blue-500"
                            />
                          </td>
                          <td className="py-1.5 px-2">
                            <input
                              type="text"
                              value={s.vendorSerialNumber || ''}
                              onChange={(e) => handleUpdateSerial(idx, 'vendorSerialNumber', e.target.value)}
                              placeholder="e.g. Dell Tag"
                              className="w-full px-2 py-1 border border-gray-200 rounded text-xs focus:ring-1 focus:ring-blue-500"
                            />
                          </td>
                          <td className="py-1.5 px-2">
                            <input
                              type="text"
                              value={s.imei || ''}
                              onChange={(e) => handleUpdateSerial(idx, 'imei', e.target.value)}
                              placeholder="Optional IMEI"
                              className="w-full px-2 py-1 border border-gray-200 rounded text-xs focus:ring-1 focus:ring-blue-500"
                            />
                          </td>
                          <td className="py-1.5 px-2">
                            <select
                              value={s.condition}
                              onChange={(e) => handleUpdateSerial(idx, 'condition', e.target.value as any)}
                              className="w-full px-2 py-1 border border-gray-200 rounded text-xs focus:ring-1 focus:ring-blue-500 bg-white"
                            >
                              <option value="GOOD">GOOD</option>
                              <option value="DAMAGED_BOX">DAMAGED BOX</option>
                              <option value="DEFECTIVE">DEFECTIVE</option>
                              <option value="REFURBISHED">REFURBISHED</option>
                            </select>
                          </td>
                          <td className="py-1.5 px-2">
                            <input
                              type="date"
                              value={s.warrantyExpiryDate || ''}
                              onChange={(e) => handleUpdateSerial(idx, 'warrantyExpiryDate', e.target.value)}
                              className="w-full px-2 py-1 border border-gray-200 rounded text-xs focus:ring-1 focus:ring-blue-500"
                            />
                          </td>
                          <td className="py-2 px-3 text-center">
                            <button
                              type="button"
                              onClick={() => handleRemoveSerial(idx)}
                              className="text-gray-400 hover:text-rose-600 transition-colors p-1"
                            >
                              <i className="ri-delete-bin-line"></i>
                            </button>
                          </td>
                        </tr>
                      ))
                    )}
                  </tbody>
                </table>
              </div>
            </div>
          )}

          {/* TAB 3: CSV IMPORT */}
          {activeTab === 'csv' && (
            <div className="space-y-4">
              <div className="bg-blue-50 border border-blue-200 rounded-xl p-3 text-xs text-blue-800">
                <span className="font-semibold block mb-1">CSV Format:</span>
                <code>SerialNumber, VendorSerialNumber, IMEI, WarrantyExpiry(YYYY-MM-DD)</code>
                <p className="mt-1 text-blue-600">One record per line. Example: <code>SN-001, 5XY8K93, 3567890123, 2029-09-01</code></p>
              </div>

              <textarea
                rows={8}
                value={csvText}
                onChange={(e) => setCsvText(e.target.value)}
                placeholder="Paste CSV lines here..."
                className="w-full p-3 text-xs font-mono border border-gray-300 rounded-xl focus:ring-2 focus:ring-blue-500 focus:outline-none"
              ></textarea>

              <button
                type="button"
                onClick={handleCsvImport}
                disabled={!csvText.trim()}
                className="px-4 py-2 bg-blue-600 hover:bg-blue-700 disabled:opacity-50 text-white rounded-xl text-xs font-semibold flex items-center gap-1.5 transition-all"
              >
                <i className="ri-upload-2-line"></i> Import Serials into List
              </button>
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="px-6 py-4 bg-gray-50 border-t border-gray-100 flex items-center justify-between">
          <div className="text-xs text-gray-500">
            {enteredCount < requiredQty ? (
              <span className="text-amber-600 font-medium">
                ⚠️ Need {requiredQty - enteredCount} more serial{requiredQty - enteredCount > 1 ? 's' : ''} to reach {requiredQty}.
              </span>
            ) : enteredCount === requiredQty ? (
              <span className="text-emerald-600 font-medium flex items-center gap-1">
                <i className="ri-checkbox-circle-fill"></i> Ready to confirm exact quantity!
              </span>
            ) : (
              <span className="text-rose-600 font-medium">
                ⚠️ Too many serials! Remove {enteredCount - requiredQty} to match quantity.
              </span>
            )}
          </div>

          <div className="flex gap-2">
            <button
              type="button"
              onClick={onClose}
              className="px-4 py-2 border border-gray-300 text-gray-700 hover:bg-gray-100 rounded-xl text-xs font-medium transition-colors"
            >
              Cancel
            </button>
            <button
              type="button"
              onClick={handleConfirm}
              disabled={!isComplete}
              className="px-5 py-2 bg-blue-600 hover:bg-blue-700 disabled:opacity-50 text-white rounded-xl text-xs font-semibold shadow-md transition-all flex items-center gap-1.5"
            >
              <i className="ri-check-line"></i> Confirm Serials ({enteredCount}/{requiredQty})
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};
